'use client';
import useDebounce from '@/_hooks/useDebounce';
import { yandexService } from '@/_service/yandex.service';
import { Box, Flex, IconButton, Input } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
// import { RiArrowDropDownLine } from 'react-icons/ri'
import AddressDropdown from './AddressDropdown';
import { ClearIcon, SearchIcon } from '@/_assets/icon-components';
import { useTranslations } from 'next-intl';
import { InputGroup } from '@/components/ui/input-group';

interface IProps {
  isIcon?: boolean;
  value: string;
  variant?: 'outline' | 'subtle' | 'flushed';
  region: [number, number] | undefined;
  onSelect: (e: any, isSelect: boolean) => void;
}

interface ISuggest {
  title: { text: string };
  subtitle: { text: string };
  distance: { text: string };
  uri: string;
}

const AddressSearch = ({
  region,
  value,
  variant = 'outline',
  isIcon,
  onSelect = () => {},
}: IProps) => {
  const [isFocus, setFocus] = useState(false);
  const [searchValue, onChange] = useState('');
  const debouncedSearch = useDebounce(searchValue, 600);
  const t = useTranslations();

  const { data: results } = useQuery({
    queryKey: ['suggest', debouncedSearch],
    queryFn: () =>
      fetchSuggest(
        {
          text: debouncedSearch,
          ull: String(region),
        },
        String(region)
      ),
    select: (res: any) => {
      return res?.response?.results?.map((item: ISuggest) => ({
        label: item?.title?.text,
        description: item?.subtitle?.text,
        distance: item?.distance?.text,
        uri: item?.uri,
      }));
    },
    retry: 1,
    enabled: Boolean(debouncedSearch && isFocus),
  });

  const getLocationByUri = async (uri: string) => {
    await fetchGeocode({ uri }).then((res: any) => {
      const result =
        res?.data?.response?.response?.GeoObjectCollection?.featureMember?.[0];

      if (result) {
        const location = result?.GeoObject?.Point?.pos?.split(' ');
        let city = '';
        if (
          result?.GeoObject?.metaDataProperty?.GeocoderMetaData?.Address
            ?.Components
        ) {
          for (const item of result?.GeoObject?.metaDataProperty
            ?.GeocoderMetaData?.Address?.Components) {
            if (item.kind === 'locality') {
              city = item.name;
            }
          }
        }
        onSelect(
          {
            address: result?.GeoObject?.name,
            city,
            location: [Number(location?.[1]), Number(location?.[0])],
          },
          true
        );
      }
    });
  };

  const onLocationSelect = (val: any, type: string) => {
    console.log('uri', val.uri);
    setFocus(false); // Close dropdown when address is selected
    onChange(val.label); // Set input value to selected address
    getLocationByUri(val.uri);
  };

  useEffect(() => {
    if (value) {
      onChange(value);
    }
  }, [value]);

  const onInputChange = (e: any) => {
    onChange(e.target.value);
  };

  return (
    <Box position={'relative'}>
      <Flex position={'relative'}>
        <InputGroup
          flex={1}
          startElement={
            isIcon && (
              <Box zIndex={2}>
                <SearchIcon />
              </Box>
            )
          }
          endElement={
            debouncedSearch && (
              <IconButton
                rounded='full'
                bg={variant}
                fontSize={'30px'}
                variant='plain'
                onClick={() => onChange('')}
                aria-label='toggle map search'
              >
                <ClearIcon color='currentColor' />
              </IconButton>
            )
          }
        >
          <Input
            _focus={{
              outline: 'primary',
              borderColor: 'primary',
            }}
            _active={{
              outline: 'primary',
              borderColor: 'primary',
            }}
            _placeholder={{
              color: 'text.muted',
            }}
            size='xl'
            rounded={16}
            h={'56px'}
            name='search'
            autoCorrect='off'
            variant={variant}
            autoComplete='off'
            value={searchValue}
            border={'1px solid gray.100'}
            onFocus={() => setFocus(true)}
            onBlur={() => setTimeout(() => setFocus(false), 500)}
            onChange={(e) => onInputChange(e)}
            placeholder={t('location.enter_address')}
            bg={variant === 'subtle' ? 'background' : 'transparent'}
          />
        </InputGroup>
      </Flex>
      {debouncedSearch && isFocus && results && (
        <AddressDropdown
          results={results}
          searchValue={searchValue}
          onSelect={onLocationSelect}
        />
      )}
    </Box>
  );
};

const fetchGeocode = async (params: any) => {
  const response = await yandexService
    .getGeocode(params)
    .catch((err) => onError(err));

  return response;
};

const fetchSuggest = async (
  text: { text: string; ull: string | undefined },
  region: string
) => {
  const response = await yandexService
    .getSuggest(text, region)
    .then((res) => res?.data)
    .catch((err) => onError(err));

  return response;
};

const onError = (err: any) => {
  if (
    err.data.message === 'Invalid key' ||
    err.data.message === 'Limit is exceeded'
  ) {
    console.log('APIKEY: ', err?.data?.message);
  }
};

export default AddressSearch;
